import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetInvoiceQuery,
  useStartInvoicePaymentMutation,
} from "../../api/billingApi";
import {
  Loader2,
  ChevronLeft,
  Download,
  ShieldCheck,
  Calendar,
  Hash,
  ArrowRight,
  Landmark,
  Wallet,
  Edit3,
  RefreshCw,
} from "lucide-react";
import { formatCurrency, formatDate } from "../../../../utils/formatters";

/** * Industrial Money Formatter */

export default function InvoicePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payAmount, setPayAmount] = useState<string>("");
  const [isEditingAmount, setIsEditingAmount] = useState(false);

  const { data: invoice, isLoading } = useGetInvoiceQuery(id!);
  const [startPayment, { isLoading: paying }] =
    useStartInvoicePaymentMutation();

  useEffect(() => {
    if (invoice && !isEditingAmount) {
      setPayAmount((invoice.balance / 100).toString());
    }
  }, [invoice, isEditingAmount]);

  const handlePay = async () => {
    if (!invoice) return;
    try {
      const res = await startPayment({
        invoiceId: invoice.id,
        amount: parseFloat(payAmount),
      }).unwrap();

      const form = document.createElement("form");
      form.method = "POST";
      form.action = res.payment_url || "https://vuvaa.com/ONEPAY/mod/live/main";

      const fields = {
        merchant_reg_id: res?.merchant_reg_id,
        amt_paid: res?.amt_paid,
        merch_trans_id: res?.merch_trans_id,
        client_phone: res?.client_phone,
        product_desc: res?.product_desc,
        client_email: res?.client_email,
        client_name: res?.client_name,
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(value ?? "");
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading || !invoice) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex flex-col items-center justify-center p-6">
        <RefreshCw className="w-8 h-8 text-primary animate-spin mb-4" />
        <span className="text-[9px] font-black uppercase tracking-[0.4em] text-slate-400">
          Loading Protocol...
        </span>
      </div>
    );
  }

  const isPaid = invoice.status === "paid";
  const isFullPayment = parseFloat(payAmount) * 100 >= invoice.balance;

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <div className="mx-auto max-w-5xl px-6 py-10">
        {/* Compact Nav */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-all"
          >
            <ChevronLeft size={14} /> Registry
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-[9px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all shadow-sm">
            <Download size={14} /> PDF
          </button>
        </div>

        {/* TERMINAL CARD */}
        <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-xl shadow-slate-200/40 overflow-hidden">
          <div className="h-2 bg-slate-900 w-full" />

          {/* Header Section */}
          <div className="p-8 md:p-10 border-b border-slate-50 bg-slate-50/20">
            <div className="flex flex-col md:flex-row justify-between items-start gap-6">
              <div className="space-y-3">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border text-[9px] font-black uppercase tracking-widest ${
                    isPaid
                      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                      : "bg-rose-50 text-rose-700 border-rose-100"
                  }`}
                >
                  <div
                    className={`w-1.5 h-1.5 rounded-full ${
                      isPaid ? "bg-emerald-500" : "bg-rose-500 animate-pulse"
                    }`}
                  />
                  {isPaid ? "Settled" : "Pending Action"}
                </div>
                <h1 className="text-3xl font-[1000] text-slate-900 tracking-tight uppercase">
                  {invoice.invoice_number}
                  <span className="text-primary italic">.</span>
                </h1>
              </div>

              <div className="flex gap-8">
                <div className="space-y-1">
                  <div className="text-[9px] font-black text-slate-300 uppercase tracking-widest flex items-center gap-1">
                    <Calendar size={12} /> Issued
                  </div>
                  <div className="text-sm font-bold text-slate-700">
                    {new Date(invoice.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[9px] font-black text-slate-300 uppercase tracking-widest flex items-center gap-1">
                    <Hash size={12} /> Origin
                  </div>
                  <div className="text-sm font-bold text-slate-700 italic">
                    {invoice?.source?.replace("_", " ") || "Official"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Area */}
          <div className="p-8 md:p-10">
            <div className="text-[9px] font-black text-slate-300 uppercase tracking-[0.3em] mb-6">
              Service Summary
            </div>

            <div className="space-y-1">
              {invoice.items.map((item: any, idx: number) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center py-4 border-b border-slate-50 hover:bg-slate-50/50 rounded-xl px-4 -mx-4 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-[10px] font-black text-slate-200">
                      0{idx + 1}
                    </div>
                    <div>
                      <div className="text-sm font-[900] text-slate-900 uppercase tracking-tight">
                        {item.description}
                      </div>
                      <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                        Year: {item.year}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-[1000] text-slate-900">
                    {formatCurrency(item.amount)}
                  </div>
                </div>
              ))}
            </div>

            {/* Calculations Grid */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
              <div
                className={`p-6 rounded-2xl border transition-all ${
                  isEditingAmount
                    ? "bg-white border-slate-900 shadow-lg"
                    : "bg-slate-50 border-transparent"
                }`}
              >
                <div className="flex justify-between items-center mb-4">
                  <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <Wallet size={14} className="text-primary" /> Pay Amount
                  </label>
                  <button
                    onClick={() => setIsEditingAmount(!isEditingAmount)}
                    className="text-[9px] font-black text-primary uppercase hover:underline"
                  >
                    {isEditingAmount ? "Done" : "Edit Amount payable"}
                  </button>
                </div>
                <div className="relative flex items-center">
                  <span className="text-xl font-black text-slate-300 mr-2">
                    ₦
                  </span>
                  <input
                    type="number"
                    disabled={!isEditingAmount}
                    className="w-full bg-transparent text-3xl font-[1000] text-slate-900 tracking-tighter outline-none disabled:text-slate-500"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-3 px-2">
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <span>Subtotal</span>{" "}
                  <span>{formatCurrency(invoice.total_amount)}</span>
                </div>
                <div className="flex justify-between text-[10px] font-black text-emerald-600 uppercase tracking-widest italic">
                  <span>Paid</span>{" "}
                  <span>- {formatCurrency(invoice.amount_paid)}</span>
                </div>
                <div className="pt-3 border-t-2 border-dashed border-slate-100 flex justify-between items-end">
                  <div className="text-[10px] font-black text-slate-900 uppercase">
                    Balance Due
                  </div>
                  <div className="text-2xl font-[1000] text-slate-900 tracking-tighter">
                    {formatCurrency(invoice.balance)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Footer */}
          <div className="p-8 bg-slate-900 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              {/* Explicit Amber/Primary color for the icon box */}
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                <ShieldCheck size={20} className="text-orange-500" />
              </div>
              <div className="hidden sm:block">
                <div className="text-[9px] font-black text-white uppercase tracking-widest">
                  Secure Gateway
                </div>
                <div className="text-[8px] font-bold text-slate-500 uppercase tracking-widest">
                  Encrypted Protocol
                </div>
              </div>
            </div>

            {invoice.balance > 0 ? (
              <button
                onClick={handlePay}
                disabled={paying}
                className="w-full md:w-auto flex items-center justify-center gap-3 bg-orange-500 text-white px-8 py-3.5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-white hover:text-slate-900 transition-all active:scale-95 group shadow-lg shadow-orange-500/20"
              >
                {paying ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    Authorize Payment
                    <ArrowRight
                      size={14}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </>
                )}
              </button>
            ) : (
              <div className="text-emerald-400 text-[9px] font-black uppercase tracking-widest flex items-center gap-2 px-4 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <ShieldCheck size={14} /> Fully Settled
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
